import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { DxTradeConversionRatesApiResponse } from '@model/DxTrade.types'
import type { CurrencyType } from '@model/WalletDto'

type RateEntry = {
  rate: number | null
  isLoading: boolean
}

type ConversionRatesState = Record<string, RateEntry>

const createConversionRatesStore = () => {
  let state: ConversionRatesState = {}
  const listeners = new Set<() => void>()
  const pendingRequests = new Set<string>()

  const getKey = (from: string, to: string) => `${from}:${to}`

  const getSnapshot = () => state

  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }

  const notify = () => {
    listeners.forEach((l) => l())
  }

  const setRate = (key: string, rate: number | null) => {
    state = { ...state, [key]: { rate, isLoading: false } }
    pendingRequests.delete(key)
    notify()
  }

  const markLoading = (key: string) => {
    if (state[key]?.isLoading) return
    state = { ...state, [key]: { rate: null, isLoading: true } }
    notify()
  }

  const hasPendingRequest = (key: string) => pendingRequests.has(key)

  const markPending = (key: string) => {
    pendingRequests.add(key)
  }

  const isResolved = (key: string) => {
    const entry = state[key]
    return entry != null && !entry.isLoading && entry.rate != null
  }

  return {
    getKey,
    getSnapshot,
    subscribe,
    setRate,
    markLoading,
    hasPendingRequest,
    markPending,
    isResolved,
  }
}

const store = createConversionRatesStore()

export const useConversionRate = (
  instrumentCurrency?: string | null,
  accountCurrency?: CurrencyType
) => {
  const apiClient = useApiClient(ClientApiClient)

  const requiresConversion = Boolean(
    instrumentCurrency && accountCurrency && instrumentCurrency !== accountCurrency
  )

  const key = useMemo(() => {
    if (!requiresConversion || !instrumentCurrency || !accountCurrency) return null
    return store.getKey(instrumentCurrency, accountCurrency)
  }, [requiresConversion, instrumentCurrency, accountCurrency])

  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot)
  const entry = key ? snapshot[key] : undefined

  const fetchRate = useCallback(async () => {
    if (!key || !instrumentCurrency || !accountCurrency) return
    if (store.hasPendingRequest(key) || store.isResolved(key)) return

    store.markPending(key)
    store.markLoading(key)

    try {
      const result: DxTradeConversionRatesApiResponse = await apiClient.getDxtradeConversionRates(
        instrumentCurrency,
        accountCurrency
      )
      const rates = result?.conversionRates
      const convRate = Array.isArray(rates) && rates.length > 0 ? rates[0]?.convRate ?? null : null
      store.setRate(key, convRate)
    } catch {
      store.setRate(key, null)
    }
  }, [key, instrumentCurrency, accountCurrency, apiClient])

  useEffect(() => {
    if (requiresConversion && key && !entry) {
      fetchRate()
    }
  }, [requiresConversion, key, entry, fetchRate])

  if (!requiresConversion) {
    return { rate: 1 as number | null, isLoading: false, requiresConversion: false }
  }

  return {
    rate: entry?.rate ?? null,
    isLoading: entry?.isLoading ?? true,
    requiresConversion,
  }
}
